import { FeatureFlagUserData, FeatureFlagUserDataPayload } from '~/src/app/utils/featureflag/types';
import { useUserAccount } from '~/src/hooks/useUserAccount';
import { UserAccount } from '~/src/services/UserAccountService';
import { environment } from '~/src/utils/environment';

const isShippoEmail = (email: string) =>
  email.endsWith('@shippo.com') || email.endsWith('@goshippo.com');

/**
 * Creates FeatrureFlagUserData from userAccount
 * @returns {FeatureFlagUserData} The user data object for feature flagging.
 */
const getFeatureFlagUserData = (data: UserAccount | undefined): FeatureFlagUserData | null => {
  const userAgent = navigator.userAgent;
  if (!data) return null;
  const {
    address,
    currentPromo,
    dateJoined,
    email,
    firstName,
    hasPurchasedLabel,
    id,
    isActive,
    lastName,
    lifetimeVolSegmentMax,
    lifetimeVolSegmentMin,
    revenueTierName,
  } = data;

  return {
    country: address && address.country ? address?.country : '',
    dateJoined: Date.parse(dateJoined),
    hasPromo: Boolean(currentPromo),
    hasPurchasedFirstLabel: Boolean(hasPurchasedLabel),
    hasPurchasedLabel,
    id,
    isActive,
    isE2EAutomatedTestUser:
      isShippoEmail(email) && (email.startsWith('test+') || email.startsWith('stester')),
    isE2EGetStartedUser: isShippoEmail(email) && email.startsWith('test-get-started+'),
    isE2ETestUser:
      (isShippoEmail(email) && email.startsWith('test+')) ||
      (firstName === 'Test' && lastName === 'Cafe'),
    isMobile: false,
    isShippoEmail: isShippoEmail(email),
    lifetimeVolSegmentMax,
    lifetimeVolSegmentMin,
    ordersCount: 0,
    primaryCarrier: '',
    proPlanVariant: '',
    registrationStore: '',
    revenueTierName,
    userAgent,
    userId: id,
    userType: '',
  };
};

/**
 * This hook is designed to retrieve user data for feature flagging.
 * - If the user is logged in, it returns the user data object for feature flagging.
 * - If the user is not logged in, it returns null.
 * @returns {FeatureFlagUserDataPayload} The user data object for feature flagging.
 */
export const useUserDataForFeatureFlag = (): FeatureFlagUserDataPayload | null => {
  const { data } = useUserAccount();
  const featureFlagUserData = getFeatureFlagUserData(data);

  if (!featureFlagUserData) return null;
  const { id, ...otherAttributes } = featureFlagUserData;

  return {
    attributes: {
      ...otherAttributes,
      anonymous: false,
      environmentName: environment.ENVIRONMENT_NAME,
    },
    id: id.toString(),
  };
};
